import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { App } from 'antd';
import { useSetState } from 'ahooks';
import { useIntl } from 'react-intl';

import { useUgcReportListCache } from '@/pages/ugc-reports/store/list-cache';
import { ugcReportList, type UgcReportTargetType, type UgcReportTicket } from '@/services/ugc-reports';
import { DEFAULT_PAGE_SIZE } from '@/pages/ugc-reports/constants';
import {
    buildUgcReportListParams,
    createDefaultSearchFormValues,
    type UgcReportSearchFormValues,
} from '@/pages/ugc-reports/utils';
import { isAbortError, RequestGuard } from '@/utils/request-guard';

import type { PaginationProps } from 'antd';
import type { FormInstance } from 'antd/es/form';

const DEFAULT_PAGINATION: PaginationProps = {
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
};

type UseUgcReportListOptions = {
    initialValues?: UgcReportSearchFormValues;
    withTimeFilter?: boolean;
};

export const useUgcReportList = (
    targetType: UgcReportTargetType,
    searchForm: FormInstance<UgcReportSearchFormValues>,
    options: UseUgcReportListOptions = {},
) => {
    const { initialValues, withTimeFilter = true } = options;
    const defaultFilters = initialValues ?? createDefaultSearchFormValues(withTimeFilter);

    const cachedEntry = useUgcReportListCache.getState().getEntry(targetType);
    const skipInitialFetchRef = useRef(!!cachedEntry);
    const prevTargetTypeRef = useRef(targetType);
    const formInitialValues = cachedEntry?.searchFilters ?? defaultFilters;

    const { message } = App.useApp();
    const { formatMessage } = useIntl();
    const requestGuard = useMemo(() => new RequestGuard(), []);

    const [dataSource, setDataSource] = useState<UgcReportTicket[]>(cachedEntry?.dataSource ?? []);
    const [loading, setLoading] = useState(false);
    const [searchFilters, setSearchFilters] = useState<UgcReportSearchFormValues>(formInitialValues);
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>(cachedEntry?.expandedRowKeys ?? []);
    const [pagination, setPagination] = useSetState<PaginationProps>(
        cachedEntry?.pagination ? { ...cachedEntry.pagination } : { ...DEFAULT_PAGINATION },
    );

    const snapshotRef = useRef({
        searchFilters: formInitialValues,
        pagination: cachedEntry?.pagination ? { ...cachedEntry.pagination } : { ...DEFAULT_PAGINATION },
        expandedRowKeys: cachedEntry?.expandedRowKeys ?? [],
        dataSource: cachedEntry?.dataSource ?? [],
    });

    const fetchList = useCallback(
        async (page: number, pageSize: number, filters = searchFilters) => {
            const { id, signal } = requestGuard.begin();
            setLoading(true);
            try {
                const { data } = await ugcReportList(
                    buildUgcReportListParams(targetType, filters, page, pageSize),
                    { signal },
                );
                if (!requestGuard.isCurrent(id)) return;

                if (data.code !== 200) {
                    message.error(data.msg);
                    return;
                }

                const tickets = data.data ?? [];
                setDataSource(tickets);
                setPagination({
                    current: Number(data.meta?.page ?? page) || 1,
                    pageSize: Number(data.meta?.size ?? pageSize) || DEFAULT_PAGE_SIZE,
                    total: data.meta?.total ?? tickets.length,
                });
            } catch (error: any) {
                if (isAbortError(error)) return;
                message.error(
                    error?.response?.data?.msg ||
                        error?.message ||
                        formatMessage({ id: 'ugcReports.fetchFailed' }),
                );
            } finally {
                if (requestGuard.isCurrent(id)) setLoading(false);
            }
        },
        [formatMessage, message, requestGuard, searchFilters, setPagination, targetType],
    );

    const handleSearch = useCallback(() => {
        const nextFilters = searchForm.getFieldsValue();
        setExpandedRowKeys([]);
        setPagination((prev) => ({ ...prev, current: 1 }));
        setSearchFilters(nextFilters);
    }, [searchForm, setPagination]);

    const handleResetSearch = useCallback(() => {
        const freshDefaults = createDefaultSearchFormValues(withTimeFilter);
        useUgcReportListCache.getState().clearEntry(targetType);
        searchForm.setFieldsValue(freshDefaults);
        setExpandedRowKeys([]);
        setPagination({ ...DEFAULT_PAGINATION });
        setSearchFilters(freshDefaults);
        setDataSource([]);
        snapshotRef.current = {
            searchFilters: freshDefaults,
            pagination: { ...DEFAULT_PAGINATION },
            expandedRowKeys: [],
            dataSource: [],
        };
    }, [searchForm, targetType, withTimeFilter]);

    const handleExpand = useCallback((expanded: boolean, record: UgcReportTicket) => {
        setExpandedRowKeys(expanded ? [record.id] : []);
    }, []);

    useEffect(() => {
        snapshotRef.current = { searchFilters, pagination, expandedRowKeys, dataSource };
    }, [searchFilters, pagination, expandedRowKeys, dataSource]);

    useEffect(() => {
        return () => {
            useUgcReportListCache.getState().setEntry(targetType, snapshotRef.current);
        };
    }, [targetType]);

    useEffect(() => {
        searchForm.setFieldsValue(formInitialValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅在挂载时同步缓存筛选到 Form
    }, []);

    useEffect(() => {
        if (skipInitialFetchRef.current) {
            skipInitialFetchRef.current = false;
            return () => requestGuard.abort();
        }
        fetchList(pagination.current as number, pagination.pageSize as number, searchFilters);
        return () => requestGuard.abort();
    }, [fetchList, requestGuard, searchFilters]);

    useEffect(() => {
        if (prevTargetTypeRef.current === targetType) {
            return;
        }
        prevTargetTypeRef.current = targetType;

        const entry = useUgcReportListCache.getState().getEntry(targetType);
        if (entry) {
            skipInitialFetchRef.current = true;
            setDataSource(entry.dataSource);
            setSearchFilters(entry.searchFilters);
            setExpandedRowKeys(entry.expandedRowKeys);
            setPagination(entry.pagination);
            searchForm.setFieldsValue(entry.searchFilters);
            return;
        }

        skipInitialFetchRef.current = false;
        setExpandedRowKeys([]);
        const freshDefaults = createDefaultSearchFormValues(withTimeFilter);
        setPagination({ ...DEFAULT_PAGINATION });
        searchForm.setFieldsValue(freshDefaults);
        setSearchFilters(freshDefaults);
        setDataSource([]);
    }, [searchForm, setPagination, targetType, withTimeFilter]);

    return {
        dataSource,
        loading,
        pagination,
        expandedRowKeys,
        fetchList,
        handleSearch,
        handleResetSearch,
        handleExpand,
        searchFilters,
        formInitialValues,
    };
};
